/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import React from 'react';
import { getGroup } from '@g17eco/core';
import { UniversalTrackerPlain } from '../../../types/universalTracker';
import { capitalise } from '../../../utils';
import SDGIcon from '../../sdg-icon/sdg-icon';
import SimpleTooltip from '../../simple-tooltip';
import UniversalTracker from '../../../model/UniversalTracker';

const Label = ({ label }: { label: string }) => (
  <div className='text-label-uppercase text-ThemeTextDark'>{label}</div>
);

const Value = ({ value }: { value: string | number | JSX.Element }) => (
  <div className='mt-1 text-ThemeTextMedium'>
    {value}
  </div>
);


function getSDGIcons(sdgs: string[] | undefined) {
  if (!sdgs || sdgs.length === 0) {
    return undefined;
  }
  return <>
    {sdgs.map((sdg: string) => (
      <SimpleTooltip key={`metric-table-sdg-icon-sdgs-${sdg}`} text={`SDG ${sdg}`}>
        <SDGIcon height='30' className='mr-2' code={sdg} />
      </SimpleTooltip>
    ))}
  </>
};

const getAlternativeInfo = (alternatives: undefined | { [key: string]: { name: string } }) => {
  if (!alternatives) {
    return;
  }

  const a = [];
  for (const [key, value] of Object.entries(alternatives)) {
    const group = getGroup('standards', key);
    if (group) {
      a.push(`${group.name} - ${value.name}`);
    }
  }
  return <>{a.map((b, i) => <div key={`alt-info-${b}-${i}`}>{b}</div>)}</>;
}

export const TrackerInformation = (props: { utr: UniversalTracker }) => {
  const { utr: universalTracker } = props;

  const utr: UniversalTrackerPlain = universalTracker.getRaw();

  const fullWidth = [
    { label: 'Standards', value: getAlternativeInfo(utr.alternatives) },
    { label: 'Related SDGs', value: getSDGIcons(utr.tags?.sdg) },
  ];

  const col1 = [
    { label: 'Metric Type', value: universalTracker.getTypeName() },
    { label: 'Value Type', value: universalTracker.getValueTypeName() },
    { label: 'Metric Unit', value: universalTracker.getMetricUnitCode() },
    { label: 'Target Direction', value: capitalise(universalTracker.getTargetDirection()) },
    { label: 'Minimum Value', value: universalTracker.getMin() },
    { label: 'Maximum Value', value: universalTracker.getMax() },
    { label: 'Suffix', value: universalTracker.getSuffix() },
  ];

  const col2 = [
    { label: 'Reference Code', value: universalTracker.getCode() },
    { label: 'Unique Identifier', value: universalTracker.getId() },
  ];

  return (
    <div className='border-top border-ThemeNeutralsLight mt-3 p-3'>
      <div className='row'>
        <div className='col-12 col-xl-5'>
          {col1.filter(row => !!row.value).map(row => (
            <div key={`add-info-${row.value}`} className='my-3'>
              <Label label={row.label} />
              <Value value={row.value ?? ''} />
            </div>
          ))}
        </div>
        <div className='col-12 col-xl-7'>
          {col2.filter(row => !!row.value).map(row => (
            <div key={row.label} className='my-3'>
              <Label label={row.label} />
              <Value value={row.value} />
            </div>
          ))}
        </div>
      </div>
      <div>
        {fullWidth.filter(row => !!row.value).map(row => (
          <div key={`add-info-${row.value}`} className='my-3'>
            <Label label={row.label} />
            <Value value={row.value ?? ''} />
          </div>
        ))}
      </div>
    </div>
  );
}
